import { NavigableModal } from '$components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { CreateClientForm, CreateClientSchema, steps } from '../forms/create'
import { MultiStepFormProvider } from '$components'
import { clientIndexQueryKey, clientStoreMutation } from '$api-client/@tanstack/react-query.gen'
import { DogBreed, Gender } from '$api-client/types.gen'
import { ClientRoutePath } from '../routes/protected'
import { QueryErrorHelper } from '$utils/query'
import { FormSteps } from '$components'
import { ToastHelper } from '$components'
import { ToastType } from '$components'

export function CreateClientModal() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleClose = () => navigate(ClientRoutePath.Clients)

  const clientMutation = useMutation({
    ...clientStoreMutation(),
    onSuccess: () => {
      ToastHelper.createSuccess(ToastType.Client)
      queryClient.invalidateQueries({ queryKey: clientIndexQueryKey() })
      handleClose()
    },
    onError: () => ToastHelper.createError(ToastType.Client),
  })

  const handleSubmit = (values: CreateClientSchema) => {
    clientMutation.mutate({
      body: {
        // Details
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        internal_notes: values.internal_notes,

        // Pets
        pets: values.pets?.map(pet => ({
          name: pet.pet_name,
          gender: pet.pet_gender as Gender,
          breed: pet.pet_breed.value as DogBreed,
          birth_date: pet.pet_birth_date?.toString(),
          size_category_id: Number(pet.pet_size_category?.id),
          coat_type_id: Number(pet.pet_coat_type?.id),
          internal_notes: pet.pet_internal_notes,
        })) ?? [],
      },
    })
  }

  return (
    <MultiStepFormProvider steps={steps}>
      {({ getCurrentStep }) => (
        <CreateClientForm
          isLoading={false}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          errors={QueryErrorHelper.getQueryValidationErrors(clientMutation.error)}
        >
          <NavigableModal onClose={handleClose}>
            <NavigableModal.Header>
              <NavigableModal.Heading>
                Create Client
              </NavigableModal.Heading>

              <FormSteps
                steps={steps}
                getCurrentStep={getCurrentStep}
              />
            </NavigableModal.Header>

            <NavigableModal.Content>
              <CreateClientForm.Content />
            </NavigableModal.Content>

            <NavigableModal.Footer>
              <CreateClientForm.Controls />
            </NavigableModal.Footer>
          </NavigableModal>
        </CreateClientForm>
      )}
    </MultiStepFormProvider>
  )
}
